import { refreshRequest } from '../auth/authApi';
import { storeAccessToken, tokenStorage } from '../auth/tokenStorage';

type AuthFetchOptions = RequestInit & {
  skipAuthRefresh?: boolean;
};

/**
 * Authenticated fetch for seawall's Bearer-protected routes (e.g. /accounts/*).
 * Attaches the in-memory access token and, on 401/403, tries one silent
 * refresh (via the HttpOnly `jwt` cookie) + retry before giving up.
 */
export async function authFetch(
  input: string,
  options: AuthFetchOptions = {},
): Promise<Response> {
  const { skipAuthRefresh, headers, ...rest } = options;
  const token = tokenStorage.getToken();

  const nextHeaders = new Headers(headers);
  if (token) {
    nextHeaders.set('Authorization', `Bearer ${token}`);
  }

  const res = await fetch(input, {
    ...rest,
    headers: nextHeaders,
    credentials: rest.credentials ?? 'include',
  });

  if ((res.status !== 401 && res.status !== 403) || skipAuthRefresh) {
    return res;
  }

  const refreshed = await refreshRequest();
  if (!refreshed?.accessToken) {
    tokenStorage.clearToken();
    return res;
  }

  storeAccessToken(refreshed.accessToken);
  const retryHeaders = new Headers(headers);
  retryHeaders.set('Authorization', `Bearer ${refreshed.accessToken}`);

  return fetch(input, {
    ...rest,
    headers: retryHeaders,
    credentials: rest.credentials ?? 'include',
  });
}
